import { useSearchParams } from 'react-router-dom'

import {
  CHANGES_PARAM,
  DEFAULT_SECTION,
  DEFAULT_SORT,
  Pages,
  SEARCH_PARAM,
  SECTION_PARAM,
  SORT_PARAM,
  Sorts,
} from '../constants'
import type { SectionType, SortType } from '../types'

const isSectionType = (value: string | null): value is SectionType =>
  value !== null && (Pages as string[]).includes(value)

const isSortType = (value: string | null): value is SortType =>
  value !== null && (Sorts as string[]).includes(value)

// Everything the pages list is filtered by lives in the URL, so any view of a
// report is a link someone else can open and see the same rows. Defaults are
// left out of the query string rather than written into it.
export const useReportQuery = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const param = searchParams.get(SECTION_PARAM)
  const activeSection: SectionType = isSectionType(param)
    ? param
    : DEFAULT_SECTION
  const changes = searchParams.get(CHANGES_PARAM) === 'true'

  const sortParam = searchParams.get(SORT_PARAM)
  const sort: SortType = isSortType(sortParam) ? sortParam : DEFAULT_SORT
  const search = searchParams.get(SEARCH_PARAM) ?? ''

  const setSection = (section: SectionType, withChanges = false) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set(SECTION_PARAM, section)

      if (withChanges) {
        next.set(CHANGES_PARAM, 'true')
      } else {
        next.delete(CHANGES_PARAM)
      }

      return next
    })
  }

  const setSort = (value: SortType) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)

      if (value === DEFAULT_SORT) {
        next.delete(SORT_PARAM)
      } else {
        next.set(SORT_PARAM, value)
      }

      return next
    })
  }

  // Typing is not navigation: the term replaces the current entry instead of
  // pushing one, or the back button would walk out of the report one keystroke
  // at a time.
  const setSearch = (value: string) => {
    const term = value.trim()

    if (term === search) {
      return
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)

        if (term) {
          next.set(SEARCH_PARAM, term)
        } else {
          next.delete(SEARCH_PARAM)
        }

        return next
      },
      { replace: true }
    )
  }

  return {
    activeSection,
    changes,
    search,
    setSearch,
    setSection,
    setSort,
    sort,
  }
}
